// services/issueApi.js
import apiRequest from "./axiosInstance";

/**
 * Fetches a list of issues with pagination
 * @param {Object} [data] - Query parameters for fetching issues
 * @param {number} [data.limit] - Number of issues to fetch per page
 * @param {number} [data.page] - Page number for pagination
 * @returns {Promise<Object>} Response object from the API request
 */
export const fetchIssues = (data) =>
  apiRequest("get", `/issues?limit=${data?.limit}&page=${data?.page}`);

/**
 * Fetches an issue by its ID
 * @param {string|number} id - Issue ID
 * @returns {Promise<Object>} Response object from the API request
 */
export const fetchIssueById = (id) => apiRequest("get", `/issues/${id}`);

/**
 * Fetches issues assigned to a specific user with optional filters.
 * @param {string|number} id - User ID to fetch issues for.
 * @param {Object} data - Filtering options.
 * @param {number} [data.limit] - Pagination limit.
 * @param {number} [data.page] - Page number.
 * @param {string} [data.key] - Sorting key.
 * @param {string} [data.direction] - Sort direction (asc or desc).
 * @param {Array} [data.journal] - Array of journal objects with `id` field.
 * @param {Array<string>} [data.task] - List of task names.
 * @param {Array<string>} [data.volIss] - Volume/Issue pairs (e.g., "vol/iss").
 * @param {string} [data.search] - Search keyword.
 * @param {string} [data.time] - Optional timestamp or time filter.
 * @returns {Promise<Object>} Response object from the API request.
 */
export const fetchIssueByUserId = (id, data) => {
  const journalQuery =
    data.journal?.map((dt) => `journal=${dt.id}`).join("&") || "";

  const taskQuery =
    data.task?.map((ce) => `task=${encodeURIComponent(ce)}`).join("&") || "";

  const volIssQuery =
    data.volIss
      ?.map((vi) => {
        const [volume, issue] = vi.split("/");

        if (
          volume &&
          issue &&
          volume !== "All" &&
          issue !== "All" &&
          volume !== "undefined" &&
          issue !== "undefined"
        ) {
          return `volume=${encodeURIComponent(
            volume
          )}&issue=${encodeURIComponent(issue)}`;
        }

        return null;
      })
      .filter(Boolean)
      .join("&") || "";

  const searchQuery = data.search
    ? `search=${encodeURIComponent(data.search)}`
    : "";

  const queryString = [
    `limit=${data.limit}`,
    `page=${data.page}`,
    `time=${data.time}`,
    `key=${data.key}`,
    `direction=${data.direction}`,
    taskQuery,
    journalQuery,
    volIssQuery,
    searchQuery,
  ]
    .filter(Boolean)
    .join("&");

  return apiRequest("post", `/issues/user/${id}?${queryString}`);
};

/**
 * Fetches unassigned issues with various filtering options.
 * @param {Object} data - Filtering options.
 * @param {number} [data.limit] - Pagination limit.
 * @param {number} [data.page] - Page number.
 * @param {string} [data.key] - Sorting key.
 * @param {string} [data.direction] - Sort direction (asc or desc).
 * @param {string} [data.userId] - User ID to exclude from assigned list.
 * @param {Array} [data.journal] - Array of journal objects with `id` field.
 * @param {Array<string>} [data.task] - List of task names.
 * @param {Array<string>} [data.volIss] - Volume/Issue pairs (e.g., "vol/iss").
 * @param {string} [data.search] - Search keyword.
 * @returns {Promise<Object>} Response object from the API request.
 */
export const fetchIssueByUnAssigned = (data) => {
  const journalQuery =
    data.journal?.map((dt) => `journal=${dt.id}`).join("&") || "";

  const taskQuery =
    data.task?.map((ce) => `task=${encodeURIComponent(ce)}`).join("&") || "";

  const searchQuery = data.search
    ? `search=${encodeURIComponent(data.search)}`
    : "";

  const volIssQuery =
    data.volIss
      ?.map((vi) => {
        const [volume, issue] = vi.split("/");

        if (
          volume &&
          issue &&
          volume !== "All" &&
          issue !== "All" &&
          volume !== "undefined" &&
          issue !== "undefined"
        ) {
          return `volume=${encodeURIComponent(
            volume
          )}&issue=${encodeURIComponent(issue)}`;
        }

        return null;
      })
      .filter(Boolean)
      .join("&") || "";

  const queryString = [
    `limit=${data.limit}`,
    `page=${data.page}`,
    `key=${data.key}`,
    `userId=${data.userId}`,
    `direction=${data.direction}`,
    volIssQuery,
    journalQuery,
    taskQuery,
    searchQuery,
  ]
    .filter(Boolean)
    .join("&");

  return apiRequest("post", `/issues/unassign?${queryString}`);
};

/**
 * Fetches all issues with full filtering capabilities.
 * @param {Object} data - Filtering options.
 * @param {number} [data.limit=10] - Pagination limit.
 * @param {number} [data.page=1] - Page number.
 * @param {Array<string>} [data.task] - List of task names.
 * @param {Array<string>} [data.volIss] - Volume/Issue pairs (e.g., "vol/iss").
 * @param {string|number} [data.id] - ID of the requester or user context.
 * @param {string} [data.search] - Search keyword.
 * @param {string} [data.key] - Sorting key.
 * @param {string} [data.direction] - Sort direction (asc or desc).
 * @param {Array<string>} [data.journal] - List of journal names.
 * @param {Array<string>} [data.team] - List of teams (filters).
 * @returns {Promise<Object>} Response object from the API request.
 */
export const fetchAllIssues = (data) => {
  const {
    limit = 10,
    page = 1,
    task = [],
    volIss = [],
    id,
    search,
    key,
    direction,
    journal,
    team,
  } = data;

  const taskQuery = task.map((t) => `task=${encodeURIComponent(t)}`).join("&");

  const journalQuery = journal?.map((dt) => `journal=${dt}`).join("&") || "";

  let teamQuery = team.includes("All")
    ? ""
    : team?.map((t) => `team=${t}`).join("&");

  const volIssQuery =
    volIss
      ?.map((vi) => {
        const [volume, issue] = vi.split("/");

        if (
          volume &&
          issue &&
          volume !== "All" &&
          issue !== "All" &&
          volume !== "undefined" &&
          issue !== "undefined"
        ) {
          return `volume=${encodeURIComponent(
            volume
          )}&issue=${encodeURIComponent(issue)}`;
        }

        return null;
      })
      .filter(Boolean)
      .join("&") || "";

  const searchQuery = search ? `search=${encodeURIComponent(search)}` : "";
   const sdPublishedOnWebS300Query =
    typeof data.issueS300Date === "boolean"
      ? `issueS300Date=${data.issueS300Date}`
      : "";
  const queryParams = [
    `limit=${limit}`,
    `page=${page}`,
    `id=${id}`,
    `key=${key}`,
    `direction=${direction}`,
    searchQuery,
    taskQuery,
    volIssQuery,
    sdPublishedOnWebS300Query,
    journalQuery,
    teamQuery,
  ]
    .filter(Boolean)
    .join("&");

  return apiRequest("post", `all/issues?${queryParams}`);
};

/**
 * Creates a new issue
 * @param {Object} issueData - Data for the new issue
 * @returns {Promise<Object>} Response object from the API request
 */
export const addIssue = (issueData) =>
  apiRequest("post", "/issues", issueData, {}, {}, true);

/**
 * Updates an existing issue
 * @param {string|number} id - Issue ID
 * @param {Object} issueData - Updated issue data
 * @returns {Promise<Object>} Response object from the API request
 */
export const updateIssue = (id, issueData) =>
  apiRequest("put", `/issues/user/${id}`, issueData);

export const updateTaskIssue = (id, issueData) =>
  apiRequest("put", `/issues/taskAssignment/${id}`, issueData);

export const updateOverviewIssue = (id, issueData) =>
  apiRequest("put", `/issues/issueOverview/${id}`, issueData);

/**
 * Deletes an issue by its ID
 * @param {string|number} id - Issue ID
 * @returns {Promise<Object>} Response object from the API request
 */
export const deleteIssue = (id) => apiRequest("delete", `/issues/${id}`);

/**
 * Issue API service module
 * @module
 */
